type LegacyWatchExpression = (() => unknown) | string;
type LegacyWatchListener = (newValue: unknown, oldValue: unknown) => void;

export type LegacyWindow = Window & {
  $rootScope?: {
    G_PERMISSIONS?: Record<string, unknown>;
    G_CompanyDetails?: unknown;
    currentuser?: string;
    $watch?: (
      watchExpression: LegacyWatchExpression,
      listener: LegacyWatchListener,
      objectEquality?: boolean
    ) => (() => void) | undefined;
  };
  siteContextService?: {
    contexts?: Record<string, string>;
    setContext?: (context: string) => void;
  };
  permissionsService?: {
    getPermission?: (
      permissionName: string
    ) => { validPermission?: boolean; role?: unknown } | undefined;
    /** Checks the raw user function list (dynamic permissions, not G_PERMISSIONS) */
    validDynamic?: (permissionString: string) => boolean;
  };
  angular?: {
    element?: (el: Element | Document | null) => {
      injector?: () => { get?: (name: string) => any };
    };
  };
};

export function getLegacyWindow(): LegacyWindow {
  return window as LegacyWindow;
}

/**
 * Standalone mode fallback store — acts as a simple in-memory $rootScope replacement.
 * When AngularJS $rootScope doesn't exist (standalone React shell), properties like
 * G_CompanyDetails, G_WORKORDER, G_PROPERTY etc. are stored here so components can
 * read/write them via getRootScopeProperty/setRootScopeProperty without errors.
 */
const _standaloneScopeStore: Record<string, unknown> = {};

function resolveRootScope(win: LegacyWindow) {
  if (win.$rootScope) return win.$rootScope;
  try {
    const injector = win.angular?.element?.(document.body)?.injector?.();
    const root = injector?.get?.('$rootScope');
    if (root) {
      win.$rootScope = root;
      return root;
    }
  } catch {
    // ignore
  }
  // Standalone mode: return the in-memory fallback store so get/set work
  return _standaloneScopeStore;
}

export function getLegacyRootScope(win: LegacyWindow = getLegacyWindow()) {
  return resolveRootScope(win);
}

export function getLegacyPermissions(
  win: LegacyWindow = getLegacyWindow()
): Record<string, unknown> {
  // Prefer Angular's $rootScope permissions, but fall back to any global copies if present.
  const root = resolveRootScope(win);
  return (
    (root?.G_PERMISSIONS as Record<string, unknown>) ||
    ((win as any).G_PERMISSIONS as Record<string, unknown>) ||
    ((win as any).$root?.G_PERMISSIONS as Record<string, unknown>) ||
    {}
  );
}
// ============================================================================
// CONTROLLED BRIDGE - Safely mutate $rootScope from React
// ============================================================================

/**
 * Allowed properties that React can safely set on $rootScope
 * This whitelist prevents arbitrary property injection
 */
const ALLOWED_ROOTSCOPE_PROPERTIES = [
  'G_CompanyDetails',
  'G_WORKORDER',
  'G_PROPERTY',
  'G_WDS',
  'G_SETTINGS',
  'G_PAGE_TITLE',
  'updateServiceArea',
  'serviceAID',
  'wdsMessage',
  'clientCodeDetail',
  'clientCode',
  'removedClaimStatusList',
  'RCTStateClean',
  'imageprefix',
  'currentuser',
  'allowedWorkOrderTransitions',
  'getAcceptAllSourceSystems',
  'maxDateQuestions',
  'phaseVOnlyPermissions',
  'ApplySTLogic',
  'responseInfo',
  'GetSearch',
] as const;

type AllowedRootScopeKey = (typeof ALLOWED_ROOTSCOPE_PROPERTIES)[number];

/**
 * Safely set a property on $rootScope with validation
 * Only allows whitelisted properties to prevent security issues
 */
/**
 * Safely read a property from $rootScope
 */
export function getRootScopeProperty<T = unknown>(
  key: string,
  win: LegacyWindow = getLegacyWindow()
): T | undefined {
  const root = resolveRootScope(win);
  if (!root) return undefined;
  return (root as any)[key] as T | undefined;
}

export function setRootScopeProperty(
  key: string,
  value: unknown,
  win: LegacyWindow = getLegacyWindow()
): boolean {
  // Validate key is allowed
  if (!ALLOWED_ROOTSCOPE_PROPERTIES.includes(key as AllowedRootScopeKey)) {
    console.warn(`[Security] Attempted to set non-whitelisted $rootScope property: ${key}`);
    return false;
  }

  const root = resolveRootScope(win);
  if (!root) {
    // Silently no-op when AngularJS is not available (standalone mode)
    return false;
  }

  try {
    (root as any)[key] = value;

    // Trigger digest cycle if $applyAsync is available
    if (typeof (root as any).$applyAsync === 'function') {
      (root as any).$applyAsync();
    }

    return true;
  } catch (error) {
    console.error(`[legacyContext] Failed to set $rootScope.${key}:`, error);
    return false;
  }
}
